import React, { ReactNode, forwardRef } from 'react';

// Shared visual contract for single-line fields and compound field triggers.
export const SINGLE_LINE_FIELD_CLASS =
  'rounded-full border border-outline-default/60 bg-input text-glyph-primary focus-standard';

// ============================================================================
// InputContainer - Unified outer wrapper for all input components
// ============================================================================
interface InputContainerProps {
  children: ReactNode;
  className?: string;
}

export const InputContainer: React.FC<InputContainerProps> = ({ children, className }) => (
  <div className={`mb-4 flex w-full flex-col ${className || ''}`}>{children}</div>
);

// ============================================================================
// InputLabel - Consistent label rendering
// ============================================================================
interface InputLabelProps {
  label?: string;
  required?: boolean;
  htmlFor?: string;
  id?: string;
  onClick?: React.MouseEventHandler<HTMLLabelElement>;
  className?: string;
}

export const InputLabel: React.FC<InputLabelProps> = ({
  label,
  htmlFor,
  id,
  onClick,
  className,
}) => {
  if (!label) return null;

  return (
    <label
      id={id}
      htmlFor={htmlFor}
      onClick={onClick}
      className={`mb-0.5 block text-small ${className || 'font-normal text-glyph-secondary'}`}
    >
      {label}
    </label>
  );
};

// ============================================================================
// InputError - Consistent error message display
// ============================================================================
interface InputErrorProps {
  error?: string;
  id?: string;
}

export const InputError: React.FC<InputErrorProps> = ({ error, id }) => {
  if (!error) return null;
  return (
    <div id={id} className="mt-1 text-small text-glyph-critical">
      {error}
    </div>
  );
};

// ============================================================================
// InputControlButton - Stepper/Date picker control buttons
// ============================================================================
interface InputControlButtonProps {
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  children: ReactNode;
  isRightmost?: boolean;
  className?: string;
  disabled?: boolean;
  onMouseDown?: React.MouseEventHandler<HTMLButtonElement>;
  onMouseUp?: React.MouseEventHandler<HTMLButtonElement>;
  onMouseLeave?: React.MouseEventHandler<HTMLButtonElement>;
  onTouchStart?: React.TouchEventHandler<HTMLButtonElement>;
  onTouchEnd?: React.TouchEventHandler<HTMLButtonElement>;
  onTouchCancel?: React.TouchEventHandler<HTMLButtonElement>;
  'aria-label'?: string;
}

export const InputControlButton: React.FC<InputControlButtonProps> = ({
  onClick,
  children,
  className,
  disabled = false,
  onMouseDown,
  onMouseUp,
  onMouseLeave,
  onTouchStart,
  onTouchEnd,
  onTouchCancel,
  'aria-label': ariaLabel,
}) => {
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      onClick={onClick}
      onMouseDown={onMouseDown}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseLeave}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      onTouchCancel={onTouchCancel}
      disabled={disabled}
      className={[
        'flex items-center justify-center p-1.5 transition-all duration-fast ease-smooth',
        'text-glyph-secondary hover:bg-surface-tertiary hover:text-glyph-primary active:bg-surface-tertiary',
        'rounded-full focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-primary',
        disabled ? 'cursor-not-allowed opacity-40 grayscale' : '',
        className || '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {children}
    </button>
  );
};

interface InputFieldProps {
  id?: string;
  name?: string;
  value: string | number;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  type?: string;
  placeholder?: string;
  hasRightControls?: boolean;
  min?: string | number;
  max?: string | number;
  maxLength?: React.InputHTMLAttributes<HTMLInputElement>['maxLength'];
  className?: string;
  disabled?: boolean;
  size?: 'md' | 'sm';
  onClick?: () => void;
  onFocus?: () => void;
  onBlur?: () => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  initialFocus?: boolean;
  ariaLabel?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
  autoComplete?: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
  autoCorrect?: React.InputHTMLAttributes<HTMLInputElement>['autoCorrect'];
  autoCapitalize?: React.InputHTMLAttributes<HTMLInputElement>['autoCapitalize'];
  spellCheck?: React.InputHTMLAttributes<HTMLInputElement>['spellCheck'];
  step?: string | number;
}

export const InputField = forwardRef<HTMLInputElement, InputFieldProps>(
  (
    {
      id,
      name,
      value,
      onChange,
      type = 'text',
      placeholder,
      hasRightControls = false,
      min,
      max,
      maxLength,
      className,
      disabled = false,
      size = 'md',
      onClick,
      onFocus,
      onBlur,
      onKeyDown,
      initialFocus = false,
      ariaLabel,
      'aria-describedby': ariaDescribedBy,
      'aria-invalid': ariaInvalid,
      autoComplete,
      inputMode,
      autoCorrect,
      autoCapitalize,
      spellCheck,
      step,
    },
    ref
  ) => {
    const isUrlInput = type === 'url';
    const inputClassNames = [
      SINGLE_LINE_FIELD_CLASS,
      'w-full',
      size === 'sm' ? 'h-9 px-3' : 'h-input-height px-4',
      'text-sm font-body text-left box-border',
      'placeholder:text-glyph-disabled/60 transition-all duration-fast ease-smooth',
      hasRightControls ? 'peer hide-spinner flex-1 rounded-r-none border-r-0' : '',
      type === 'number' && !hasRightControls ? 'hide-spinner' : '',
      type === 'date' ? 'cursor-pointer' : '',
      className || '',
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <input
        id={id}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        onClick={onClick}
        onFocus={onFocus}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        min={min}
        max={max}
        maxLength={maxLength}
        step={step}
        inputMode={inputMode ?? (isUrlInput ? 'url' : undefined)}
        className={inputClassNames}
        placeholder={placeholder}
        disabled={disabled}
        ref={ref}
        data-modal-initial-focus={initialFocus ? 'true' : undefined}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        aria-invalid={ariaInvalid}
        autoComplete={autoComplete ?? (isUrlInput ? 'off' : undefined)}
        autoCorrect={autoCorrect ?? (isUrlInput ? 'off' : undefined)}
        autoCapitalize={autoCapitalize ?? (isUrlInput ? 'none' : undefined)}
        spellCheck={spellCheck ?? (isUrlInput ? false : undefined)}
      />
    );
  }
);
